import { Button, Modal, Spinner } from "src/components";
import { useEffect, useState } from "react";
import { getProductsReport } from "src/services/reports-api";
import moment from "moment";
import { useToast } from "src/hooks/useToast";
import Pagination from "src/components/ui/Pagination";
import FeedbackReportFilters from "./FeedbackReportFilters";
import FormSuccess from "src/features/fields/container/FormSuccess";

const PAGE_SIZE = 10;

const FeedbackReport = () => {
    const [reports, setReports] = useState<any>([]);
    const [isFetching, setIsFetching] = useState(false);
    const [pageNo, setPageNo] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const { toast } = useToast();
    const [openFilterModal, setOpenFilterModal] = useState(false);
    const [successModalOpen, setSuccessModalOpen] = useState(false);

    const handleReportFilterSubmit = () => {
        setOpenFilterModal(false);
        setSuccessModalOpen(true);
    }

    const fetchReports = async () => {   
        try {
            setIsFetching(true);
            const res = await getProductsReport({ page: pageNo, limit: PAGE_SIZE, report_type: "feedback" });
            setReports(res?.data?.data?.reports);
            setTotalPages(res?.data?.data?.totalPages || 1);
            setTotal(res?.data?.data?.total || 0);
            toast({ description: "Reports fetched successfully", variant: "default" });
        } catch (error: any) {
            console.log(error);
            toast({ description: error?.response?.data?.message || "Failed to fetch reports", variant: "destructive" });
        } finally {
            setIsFetching(false);
        }
    }

    useEffect(() => {
        fetchReports();
    }, [pageNo]);

    return (
        <div>
            <header className="flex flex-col justify-between py-6 mx-auto max-h-[100vh] shadow-md">
                <div className="flex justify-between px-[3.75rem]">
                    <div className="">
                        <h1 className="text-[#49484C] font-bold text-[2rem]">Feedback</h1>
                    </div>
                </div>
            </header>
            <div className="mt-4 px-[5.75rem] p-6">
                <div className="flex flex-col justify-between  gap-2">
                    <div className="flex justify-end items-center gap-2">
                        <div className="flex gap-2 justify-end">
                            <Button onClick={() => setOpenFilterModal(true)} className="font-normal text-white bg-[#8051FF]">
                                Generate Report
                            </Button>
                            <Button
                                onClick={() => {
                                    setIsFetching(true);
                                    fetchReports()
                                }}
                                isLoading={isFetching}
                                loaderSize="small"
                                className="font-normal text-black bg-[#F1F1F1]">
                                Refresh
                            </Button>
                        </div>
                    </div>
                    <div className="bg-white rounded-2xl mt-4 shadow p-8 w-[100%] border">
                        <h2 className="font-bold text-lg text-[#49484C] mb-4">Generated Reports</h2>
                        {isFetching ? <div className="flex items-center justify-center h-[20rem]"><Spinner /></div> : (reports?.length ? (
                            <>
                                <table className="min-w-full mt-5 border-2 border-gray-200">
                                    <thead className="w-full bg-gray-100">
                                        <tr>
                                            <th className="px-2 py-2 text-sm text-left text-gray-600 border-l">Request Code</th>
                                            <th className="px-2 py-2 text-sm text-left text-gray-600 border-l">Requested By</th>
                                            <th className="px-2 py-2 text-sm text-left text-gray-600 border-l">Created Date</th>
                                            <th className="px-2 py-2 text-sm text-left text-gray-600 border-l">Requested Date</th>
                                            <th className="px-2 py-2 text-sm text-left text-gray-600 border-l">Status</th>
                                            <th className="px-2 py-2 text-sm text-left text-gray-600 border-l">Report Type</th>
                                            <th className="px-2 py-2 text-sm text-left text-gray-600 border-l whitespace-nowrap">File</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {reports?.map((item: any, index: number) => {
                                            const [start, end] = item?.time_range?.split(/-(?=\d{4}-\d{2}-\d{2}T)/) || [];
                                            return (
                                                <tr key={index} className="text-sm text-[#303030]">
                                                    <td className="px-2 py-2 border-t border-l">{item?.request_code}</td>
                                                    <td className="px-2 py-2 border-t border-l">{item?.name}</td>
                                                    <td className="px-2 py-2 border-t border-l">{moment(item?.created_date).format("MMMM Do YYYY") + " " + moment(item?.created_date).format("h:mm a")}</td>
                                                    <td className="px-2 py-2 border-t border-l">{moment(start).format("MMMM Do YYYY")} - {moment(end).format("MMMM Do YYYY")}</td>
                                                    <td className="px-2 py-2 border-t border-l">{item?.status}</td>
                                                    <td className="px-2 py-2 border-t border-l">{item?.report_type}</td>
                                                    <td className="px-2 py-2 border-t border-l whitespace-nowrap w-[8rem]">
                                                        {item?.s3_file_link ? <div className="flex items-center gap-2 cursor-pointer" onClick={() => window.open(item?.s3_file_link, "_blank")}>
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 32 32"><defs><linearGradient id="vscodeIconsFileTypeExcel0" x1="4.494" x2="13.832" y1="-2092.086" y2="-2075.914" gradientTransform="translate(0 2100)" gradientUnits="userSpaceOnUse"><stop offset="0" stop-color="#18884f" /><stop offset=".5" stop-color="#117e43" /><stop offset="1" stop-color="#0b6631" /></linearGradient></defs><path fill="#185c37" d="M19.581 15.35L8.512 13.4v14.409A1.19 1.19 0 0 0 9.705 29h19.1A1.19 1.19 0 0 0 30 27.809V22.5Z" /><path fill="#21a366" d="M19.581 3H9.705a1.19 1.19 0 0 0-1.193 1.191V9.5L19.581 16l5.861 1.95L30 16V9.5Z" /><path fill="#107c41" d="M8.512 9.5h11.069V16H8.512Z" /><path d="M16.434 8.2H8.512v16.25h7.922a1.2 1.2 0 0 0 1.194-1.191V9.391A1.2 1.2 0 0 0 16.434 8.2" opacity="0.1" /><path d="M15.783 8.85H8.512V25.1h7.271a1.2 1.2 0 0 0 1.194-1.191V10.041a1.2 1.2 0 0 0-1.194-1.191" opacity="0.2" /><path d="M15.783 8.85H8.512V23.8h7.271a1.2 1.2 0 0 0 1.194-1.191V10.041a1.2 1.2 0 0 0-1.194-1.191" opacity="0.2" /><path d="M15.132 8.85h-6.62V23.8h6.62a1.2 1.2 0 0 0 1.194-1.191V10.041a1.2 1.2 0 0 0-1.194-1.191" opacity="0.2" /><path fill="url(#vscodeIconsFileTypeExcel0)" d="M3.194 8.85h11.938a1.193 1.193 0 0 1 1.194 1.191v11.918a1.193 1.193 0 0 1-1.194 1.191H3.194A1.19 1.19 0 0 1 2 21.959V10.041A1.19 1.19 0 0 1 3.194 8.85" /><path fill="#fff" d="m5.7 19.873l2.511-3.884l-2.3-3.862h1.847L9.013 14.6c.116.234.2.408.238.524h.017q.123-.281.26-.546l1.342-2.447h1.7l-2.359 3.84l2.419 3.905h-1.809l-1.45-2.711A2.4 2.4 0 0 1 9.2 16.8h-.024a1.7 1.7 0 0 1-.168.351l-1.493 2.722Z" /><path fill="#33c481" d="M28.806 3h-9.225v6.5H30V4.191A1.19 1.19 0 0 0 28.806 3" /><path fill="#107c41" d="M19.581 16H30v6.5H19.581Z" /></svg>
                                                        </div> : <span className="text-[#96969B]">File not found</span>}
                                                    </td>

                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>

                            </>
                        ) : (
                            <div className="flex flex-col items-center justify-center h-32 text-[#96969B]">
                                <span className="material-icons text-4xl mb-2">info</span>
                                No reports found. Please try again.
                            </div>
                        ))}
                    </div>
                    <Pagination
                        currentPage={pageNo}
                        totalPages={totalPages}
                        totalItems={total}
                        pageSize={PAGE_SIZE}
                        onPageChange={(page) => setPageNo(page)}
                    />
                    <Modal
                        isOpen={openFilterModal}
                        onCloseModal={() => setOpenFilterModal(false)}
                        title="Filter"
                        isHeader={false}
                    >
                        <FeedbackReportFilters
                            onCloseModal={() => setOpenFilterModal(false)}
                            onSuccess={() => {
                                handleReportFilterSubmit();
                            }}
                        />
                    </Modal>
                    <FormSuccess
                        showForm={successModalOpen}
                        onContinue={() => {
                            setSuccessModalOpen(false)
                            fetchReports();
                        }}
                        onCloseModal={() => { }}
                        variant="default"
                        popupMessage={{
                            description: "Report is being generated. Please check back in 5 minutes or click Refresh after that time.",
                            title: "Success!",
                        }}
                    />
                </div>
            </div>
        </div >
    );
};

export default FeedbackReport;